import React from "react";
import { ShadowDomContainer } from "./ShadowDomContainer";
import { autoUpdate, computePosition, flip, shift, offset, arrow } from "@floating-ui/react";

export interface UserInputConfirmationProps {
  target: Element;
  onProceed: () => void;
  proceedButtonText?: string;
  placement?: "top" | "bottom" | "left" | "right";
}

export const UserInputConfirmation: React.FC<UserInputConfirmationProps> = ({
  target,
  onProceed,
  proceedButtonText = "Proceed",
  placement = "bottom",
}) => {
  const [buttonElement, setButtonElement] = React.useState<HTMLButtonElement | null>(null);
  const [arrowElement, setArrowElement] = React.useState<HTMLDivElement | null>(null);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [arrowPosition, setArrowPosition] = React.useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!target || !buttonElement) return;

    const updatePosition = async () => {
      const {
        x,
        y,
        placement: finalPlacement,
        middlewareData,
      } = await computePosition(target, buttonElement, {
        placement,
        middleware: [offset(12), flip(), shift({ padding: 8 }), arrow({ element: arrowElement })],
      });

      setPosition({ x, y });

      if (middlewareData.arrow && arrowElement) {
        const { x: arrowX, y: arrowY } = middlewareData.arrow;

        // Calculate arrow position relative to button position
        const arrowAbsoluteX = x + (arrowX || 0);
        const arrowAbsoluteY = y + (arrowY || 0);

        const side = finalPlacement.split("-")[0];

        // Adjust arrow position to connect seamlessly with button
        let adjustedX = arrowAbsoluteX;
        let adjustedY = arrowAbsoluteY;

        switch (side) {
          case "top":
            adjustedY = y + buttonElement.offsetHeight - 4; // Position at bottom edge of button
            break;
          case "bottom":
            adjustedY = y - 4; // Position at top edge of button
            break;
          case "left":
            adjustedX = x + buttonElement.offsetWidth - 4; // Position at right edge of button
            break;
          case "right":
            adjustedX = x - 4; // Position at left edge of button
            break;
        }

        setArrowPosition({ x: adjustedX, y: adjustedY });
      }
    };

    const cleanup = autoUpdate(target, buttonElement, updatePosition);
    return cleanup;
  }, [target, buttonElement, arrowElement, placement]);

  return (
    <ShadowDomContainer>
      <button
        ref={setButtonElement}
        onClick={onProceed}
        className="pyne-user-input-confirmation__button"
        style={{
          position: "fixed",
          top: position.y,
          left: position.x,
          zIndex: "var(--pyne-layer-popover)",
          backgroundColor: "var(--pyne-primary, #3b82f6)",
          color: "white",
          border: "none",
          borderRadius: "6px",
          padding: "8px 16px",
          fontSize: "14px",
          fontWeight: "500",
          cursor: "pointer",
          transition: "all 0.2s ease",
          outline: "none",
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          pointerEvents: "auto",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.9";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow = "0 0 0 2px rgba(66, 153, 225, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)";
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
        }}
      >
        {proceedButtonText}
      </button>
      <div
        ref={setArrowElement}
        className="pyne-user-input-confirmation__arrow"
        style={{
          position: "fixed",
          top: arrowPosition.y,
          left: arrowPosition.x,
          zIndex: "var(--pyne-layer-popover)",
          width: "8px",
          height: "8px",
          backgroundColor: "var(--pyne-primary, #3b82f6)",
          transform: "rotate(45deg)",
          pointerEvents: "none",
        }}
      />
    </ShadowDomContainer>
  );
};
